import { joinSurveyAction } from '../join-survey';
import type { ConnectChannel } from '../types';

/**
 * Static content for the Media channel detail page (`/connect/media`). Same
 * sourcing rule as demographics.ts: copied, not imported, since the source
 * (Data Catalog / EXPLORE_CHANNELS) backs internal-only tooling and this
 * page is visible to every Home user.
 */
export const MEDIA_CHANNEL: ConnectChannel = {
    name: 'Media',
    icon: 'play-circle',
    color: '#639D84',
    subtitle: 'US · streaming, news, video, gaming apps',
    subtitleLine2: 'Daily · Since 2018',
    exploreDaniPrompt: 'What can media viewing data tell me about my consumers?',
    properties: [
        { name: 'YouTube', letter: 'YT', color: '#FF0000', icon: 'logos:youtube-icon' },
        { name: 'Netflix', letter: 'N', color: '#E50914', icon: 'logos:netflix-icon' },
        {
            name: 'Amazon Prime',
            letter: 'Pr',
            color: '#00A8E1',
            icon: 'selfhst:amazon-prime-video',
        },
        { name: 'Disney+', letter: 'D+', color: '#113CCF', icon: 'selfhst:disney-plus' },
    ],
    actions: [
        {
            id: 'media-viewing',
            name: 'Map viewing',
            icon: 'play-circle',
            summary: 'where your audience actually watches: YouTube, Netflix or Prime',
            template:
                'Where does {audience|my audience} actually watch: YouTube, Netflix, or Prime?',
        },
        {
            id: 'media-reach',
            name: 'Measure reach',
            icon: 'layers',
            summary: 'how far your brand reaches across streaming, news, video and gaming',
            template:
                "What's the reach of {brand|my brand} across streaming, news, video and gaming over {window|the last 12 weeks}?",
        },
        {
            id: 'media-affinity',
            name: 'Find affinity',
            icon: 'heart',
            summary: 'which media properties your audience over-indexes on',
            template: 'Which media properties over-index with {audience|my target audience}?',
        },
        joinSurveyAction('media'),
    ],
    schema: [
        { field: 'consumer_id', type: 'string', description: 'Pseudonymous panel ID' },
        { field: 'property', type: 'string', description: 'App or site name' },
        { field: 'media_type', type: 'string', description: 'Video / Audio / News / Gaming' },
        { field: 'session_date', type: 'date', description: 'Session date' },
        { field: 'total_minutes', type: 'float', description: 'Session length' },
        { field: 'daypart', type: 'string', description: 'Daypart bucket' },
    ],
    sampleCols: ['Property', 'Type', 'Date', 'Minutes', 'Daypart'],
    sampleColWidths: ['minmax(90px, 1fr)', '80px', '90px', '80px', '90px'],
    sampleRows: [
        ['YouTube', 'Video', '2026-04-22', '42.1 min', 'Evening'],
        ['Netflix', 'Video', '2026-04-22', '58.7 min', 'Late Night'],
        ['Hulu', 'Video', '2026-04-21', '33.2 min', 'Evening'],
        ['CNN', 'News', '2026-04-21', '6.4 min', 'Mid-Day'],
        ['Twitch', 'Gaming', '2026-04-20', '27.5 min', 'Evening'],
    ],
};
